'use es6';

import { createSelector } from 'reselect';
import { getHasBlogPostsPublishScope, getIsUngatedForCMPPublishing } from './authSelectors';
import { getIsPublished } from 'ContentEditorUI/redux/selectors/publishSelectors';
import { getPublishDate, getShouldPublishImmediately } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getCurrentBlogDomain, getIsBlogOnPrimaryDomainAfterMultiDomainScopeLoss, getCurrentBlogDomainIsNotPermitted } from './blogSelectors';
import { getCannotPublishOnDomainReason } from 'ContentEditorUI/utils/validationUtils';
import { getHasAccountTrustScope, isSocialAdminUser, isSocialAllAccountsUser, getHasBlogSchedulingUpdateAccess } from 'ContentEditorUI/redux/selectors/authSelectors';
import cannotPublishOnDomainReasons from 'ContentEditorUI/constants/cannotPublishOnDomainReasons';
import { getIsSchedulingAlreadyPublished as getBaseIsSchedulingAlreadyPublished, getIsPublishEnabledBase } from 'ContentEditorUI/redux/selectors/publishSelectors';
import { publishDateIsBackdated } from 'ContentEditorUI/utils/publishUtils';
import { getAccountVerificationStatus } from 'ContentEditorUI/redux/selectors/accountVerificationStatusSelectors';
import ContentRoutes from 'ContentUtils/Routes';
import { getDomainExclusionReason } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
import { getDomainExclusionReasonRequest } from 'ContentEditorUI/redux/selectors/requestStatesSelectors';
import { requestSucceeded } from 'redux-request-state/util/requestStateCheckers';
import { getCurrentBlogId } from './blogSelectors';
import { getHasExceededMlangBlogPostLimit } from './blogLimitsSelectors';
import { getPortalHasBlogCustomDomainAccess } from 'ContentEditorUI/redux/selectors/portalSelectors';
export const getHasPermissionsToPublish = createSelector([getHasBlogPostsPublishScope, getCurrentBlogDomainIsNotPermitted, getHasAccountTrustScope], (hasBlogPublishPermissionsScope, currentDomainIsNotPermitted, hasAccountTrust) => hasAccountTrust && hasBlogPublishPermissionsScope && !currentDomainIsNotPermitted);
export const getIsPublishEnabled = createSelector([getIsPublishEnabledBase, getHasPermissionsToPublish, getHasExceededMlangBlogPostLimit], (isPublishEnabledBase, hasPermissionsToPublish, hasExceededMlangBlogPostLimit) => Boolean(isPublishEnabledBase && hasPermissionsToPublish && !hasExceededMlangBlogPostLimit));
export const getIsUnpublishEnabled = createSelector([getIsPublished, getHasPermissionsToPublish], (isPublished, hasPermissionsToPublish) => isPublished && hasPermissionsToPublish);
export const getPublishValidationErrors = createSelector([getPublishDate, getShouldPublishImmediately, getHasAccountTrustScope, getAccountVerificationStatus, getIsBlogOnPrimaryDomainAfterMultiDomainScopeLoss, getPortalHasBlogCustomDomainAccess, getIsUngatedForCMPPublishing], (publishDate, publishImmediately, hasAccountTrust, accountVerificationStatus, isBlogOnPrimaryDomainAfterMultiDomainScopeLoss, hasBlogCustomDomainAccess, isUngatedForCMPPublishing) => {
  const validationErrors = {};
  if (!publishImmediately && !publishDate && !isUngatedForCMPPublishing) {
    validationErrors.publishDate = {
      pane: 'publishing',
      type: 'required',
      messageKey: `publish.scheduleRequiredDateError`
    };
  }
  if (!hasAccountTrust) {
    validationErrors.hasLowAccountTrust = {
      pane: 'publishing',
      type: 'accountVerificationSuspended',
      messageKey: `accountVerification.${accountVerificationStatus}.actionTooltip`
    };
  }
  if (!isBlogOnPrimaryDomainAfterMultiDomainScopeLoss && hasBlogCustomDomainAccess) {
    validationErrors.blogNotOnPrimaryAfterScopeLoss = {
      pane: 'publishing',
      type: 'blogNotOnPrimaryAfterScopeLoss',
      message: 'postSettings.domain.blogNotOnPrimaryDomain',
      useHtml: true,
      data: {
        blogSettingsUrl: ContentRoutes.settings('blog')
      }
    };
  }
  return validationErrors;
});
export const getCannotPublishOnActiveDomainReason = createSelector([getHasPermissionsToPublish, getCurrentBlogDomain, getHasBlogPostsPublishScope, getCurrentBlogDomainIsNotPermitted, getDomainExclusionReason], (hasPermissionsToPublish, blogDomain, userHasPublishAccess, domainIsNotPermitted, domainExclusionReason) => {
  if (hasPermissionsToPublish) {
    return '';
  }
  return getCannotPublishOnDomainReason({
    domain: blogDomain,
    domainIsNotPermitted,
    domainExclusionReason,
    contentTypeStr: 'BlogPost',
    userHasPublishAccess,
    isBlog: true
  });
});
export const getCannotPublishOnDomainReasonMessageOptions = createSelector([getCannotPublishOnActiveDomainReason, getCurrentBlogId], (cannotPublishOnActiveDomainReason, currentBlogId) => {
  const messageOptions = {};
  if (cannotPublishOnActiveDomainReason === cannotPublishOnDomainReasons.ACTIVE_DOWNGRADE_BLOG) {
    messageOptions.blogSettingsUrl = ContentRoutes.settings('blog', currentBlogId);
  }
  return messageOptions;
});
export const getCannotPublishReasonShouldAllowSwitchDomain = createSelector([getCannotPublishOnActiveDomainReason, getDomainExclusionReasonRequest, getCurrentBlogDomainIsNotPermitted], (cannotPublishOnDomainReason, domainExclusionReasonRequest, currentBlogDomainIsNotPermitted) => !currentBlogDomainIsNotPermitted || requestSucceeded(domainExclusionReasonRequest) && (!cannotPublishOnDomainReason || cannotPublishOnDomainReason !== cannotPublishOnDomainReasons.USER_HAS_NO_PUBLISH_ACCESS && cannotPublishOnDomainReason !== cannotPublishOnDomainReasons.NO_DOMAIN_PERMISSION));
export const getCanCustomizeSocial = createSelector([isSocialAdminUser, isSocialAllAccountsUser], (isAdmin, isAllAccounts) => isAdmin || isAllAccounts);
export const getIsSchedulingUpdate = createSelector([getBaseIsSchedulingAlreadyPublished, getPublishDate, getHasBlogSchedulingUpdateAccess], (baseIsSchedulingAlreadyPublished, publishDate, hasBlogSchedulingUpdateAccess) => {
  const backdated = publishDateIsBackdated(publishDate);
  return hasBlogSchedulingUpdateAccess && baseIsSchedulingAlreadyPublished && !backdated;
});